import React from 'react';
import { useTranslation } from 'react-i18next';

import AboutUsContent from '../components/Content/AboutUsContent';
import { aboutDocument } from '../content/about';
import { buildPolicyToc, enrichPolicyDocument } from '../content/legal/slug';
import MarketingPageFooter from '../components/marketing/MarketingPageFooter';
import MarketingLandingNav, { MARKETING_NAV_TOP_OFFSET } from '../components/marketing/MarketingLandingNav';
import PolicyDocumentRenderer from '../components/PolicyContent/PolicyDocumentRenderer';
import PolicyDocumentToc from '../components/PolicyContent/PolicyDocumentToc';
import { SEO } from '../shared/components/SEO';
import { buildBreadcrumbJsonLd } from '../shared/seo/breadcrumbJsonLd';
import { isEnglishLocale } from '../i18n/localeUtils';
import { getLocalizedAboutSeo } from '../content/i18n/marketingPageSeo';

interface AboutUsPageProps {
  onNavigate?: (view: string) => void;
}

const foundersPersonJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Dharmendra Singh',
    jobTitle: 'Founder',
    url: 'https://www.gograbjob.com/about-us',
    image: 'https://www.gograbjob.com/team/dharmendra-singh.png',
    sameAs: ['https://www.linkedin.com/in/dharmendra-s-77297014/'],
    worksFor: { '@id': 'https://www.gograbjob.com/#organization' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Ramesh Behera',
    jobTitle: 'Co-Founder',
    url: 'https://www.gograbjob.com/about-us',
    image: 'https://www.gograbjob.com/team/ramesh-behera.png',
    sameAs: ['https://www.linkedin.com/in/ramesh-behera-97386616/'],
    worksFor: { '@id': 'https://www.gograbjob.com/#organization' },
  },
];

const AboutUsPage: React.FC<AboutUsPageProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const useLocalizedBody = !isEnglishLocale(i18n.language);
  const seo = getLocalizedAboutSeo(t);
  const doc = enrichPolicyDocument(aboutDocument);
  const toc = buildPolicyToc(doc.sections);

  return (
    <div className="policy-doc-page policy-doc-page--about min-h-screen flex flex-col">
      <SEO
        title={useLocalizedBody ? t('views.about.title') : seo.title}
        description={useLocalizedBody ? t('views.about.subtitle') : seo.description}
        canonical="https://www.gograbjob.com/about-us"
        includeGeo
        jsonLd={[
          buildBreadcrumbJsonLd([
            { name: t('common.home', { defaultValue: 'Home' }), path: '/' },
            { name: useLocalizedBody ? t('views.about.title') : seo.breadcrumb },
          ]),
          ...foundersPersonJsonLd,
        ]}
      />

      <MarketingLandingNav onNavigate={onNavigate} variant="marketing" />

      <main className={`policy-doc-page__main ${MARKETING_NAV_TOP_OFFSET}`} key={i18n.language}>
        {useLocalizedBody ? (
          <div className="policy-doc-layout policy-doc-layout--localized">
            <div className="policy-doc-layout__content policy-doc-layout__content--full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <AboutUsContent />
            </div>
          </div>
        ) : (
          <div className="policy-doc-layout">
            <PolicyDocumentToc entries={toc} />
            <div className="policy-doc-layout__content">
              <PolicyDocumentRenderer
                eyebrow={doc.eyebrow}
                title={doc.title}
                effectiveDate={doc.effectiveDate}
                lastUpdated={doc.lastUpdated}
                summary={doc.summary}
                sections={doc.sections}
                metaPrimaryLabel="Founded"
                metaSecondaryLabel="Page updated"
              />
            </div>
          </div>
        )}
      </main>

      <MarketingPageFooter onNavigate={onNavigate} theme="about" />
    </div>
  );
};

export default AboutUsPage;
